import type { PropsWithChildren, ReactNode } from "react";
import React from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { showTitleWhenClipped } from "utils/showTitleWhenClipped";
import type { OverflowMenuItem } from "components/OverflowMenu/OverflowMenu";
import { OverflowMenu } from "components/OverflowMenu/OverflowMenu";
import { withTheme } from "components/Theme";

type DetailsPageLayoutProps = PropsWithChildren<{
  pageName: string;
  pageNameSx?: SxProps<Theme>;
  titleIcon?: ReactNode;
  titleBadge?: ReactNode;
  actions?: ReactNode[];
  overflowMenuItems?: OverflowMenuItem[];
}>;

export function DetailsPageLayout(props: DetailsPageLayoutProps) {
  const { pageName, pageNameSx, titleIcon, titleBadge, actions, overflowMenuItems, children } = props;

  return withTheme((theme) => (
    <Stack direction="column" spacing={children ? 3 : 0}>
      <Grid
        container
        spacing={0}
        sx={{
          height: "2.5rem",
        }}
      >
        <Grid
          size={4}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
          }}
        >
          {titleIcon && <Box sx={{ display: "flex", flexShrink: 0 }}>{titleIcon}</Box>}
          <Typography
            variant={"h5"}
            noWrap
            sx={{ color: theme.primaryText, minWidth: 0, ...pageNameSx }}
            onMouseEnter={showTitleWhenClipped(pageName)}
          >
            {pageName}
          </Typography>
          {titleBadge && <Box sx={{ display: "flex", flexShrink: 0 }}>{titleBadge}</Box>}
        </Grid>
        {((actions && actions.length > 0) || (overflowMenuItems && overflowMenuItems.length > 0)) && (
          <Grid
            size={8}
            sx={{
              display: "flex",
              justifyContent: "end",
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              sx={{
                alignItems: "center",
              }}
            >
              {actions}
              {overflowMenuItems && <OverflowMenu key="overflowMenu" items={overflowMenuItems} />}
            </Stack>
          </Grid>
        )}
      </Grid>
      {children && <Box>{children}</Box>}
    </Stack>
  ));
}
